import { useState } from 'react';
import { useTranslate, useNavigation } from '@refinedev/core';
import type { CrudFilter } from '@refinedev/core';
import { List } from '@refinedev/antd';
import { Table, Input, Form, Row, Col, Typography, Button } from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import type { ContentImprintOut } from '@/api/generated/schemas';
import { useTableSearch } from '@/hooks/useTableSearch';
import { useTableRowNavigateToShow } from '@/hooks/useTableRowNavigateToShow';
import { useAdminTable } from '@/hooks/useAdminTable';
import { DateRangeFilter } from '@/components/common/DateRangeFilter';
import { isFieldFiltered } from '@/utils/filters';
import { getSortOrder } from '@/utils/sorters';
import { ImprintCreateModal } from '@/components/content_catalog/imprints/ImprintCreateModal';

export const ContentCatalogImprintList = () => {
  const t = useTranslate();
  const { showUrl } = useNavigation();
  const [createOpen, setCreateOpen] = useState(false);
  const rowNav = useTableRowNavigateToShow();
  const { onSearch, createDebouncedSubmit } = useTableSearch();

  const { tableProps, searchFormProps, sorters, setFilters, filters } =
    useAdminTable<ContentImprintOut>({
      pagination: { pageSize: 20 },
      resource: 'content_catalog.imprints',
      onSearch,
      sorters: {
        initial: [{ field: 'id', order: 'desc' }],
      },
    });

  const debouncedSubmit = createDebouncedSubmit(searchFormProps.form);

  return (
    <List
      canCreate={false}
      headerButtons={({ defaultButtons }) => (
        <>
          {defaultButtons}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateOpen(true)}
          >
            {t('content_catalog.imprints.create.button')}
          </Button>
        </>
      )}
    >
      <ImprintCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
      <Form {...searchFormProps} layout="vertical">
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }} align="middle">
          <Col xs={24} sm={16} md={12} lg={8}>
            <Form.Item name="q" style={{ marginBottom: 0 }}>
              <Input
                placeholder={t('content_catalog.imprints.search.placeholder')}
                prefix={<SearchOutlined />}
                allowClear
                onChange={(e) => debouncedSubmit(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Table
        {...rowNav('content_catalog.imprints', tableProps)}
        rowKey="id"
        pagination={{
          ...(typeof tableProps.pagination === 'object'
            ? tableProps.pagination
            : {}),
          showTotal: (total) => t('base.total', { total }),
        }}
      >
        <Table.Column
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
        />

        <Table.Column
          dataIndex="name"
          title={t('content_catalog.imprints.fields.name')}
          sorter
          sortOrder={getSortOrder(sorters, 'name')}
        />

        <Table.Column
          dataIndex="publisher_name"
          title={t('content_catalog.imprints.fields.publisher')}
          render={(value: string | null, record: ContentImprintOut) => (
            <Typography.Link
              href={showUrl('content_catalog.publishers', record.publisher_id)}
              onClick={(e) => e.stopPropagation()}
            >
              {value ?? `#${record.publisher_id}`}
            </Typography.Link>
          )}
        />

        <Table.Column
          dataIndex="created_at"
          title={t('base.fields.created_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'created_at')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'created_at')}
          filterDropdown={({ confirm, clearFilters }) => (
            <DateRangeFilter
              field="created_at"
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: string) =>
            value ? new Date(value).toLocaleString() : '-'
          }
        />
      </Table>
    </List>
  );
};
